import { useCount, useLiteratureCount } from '@/components/count';
import { CitationIcon, FeatureList, GenericFeature, OccurrenceIcon } from '@/components/highlights';
import { Tabs } from '@/components/tabs';
import { ProjectPageFragment } from '@/gql/graphql';
import { DynamicLink } from '@/reactRouterPlugins';
import { fragmentManager } from '@/services/fragmentManager';
import { MdCalendarMonth as CalendarIcon, MdEuro as EuroIcon, MdLink } from 'react-icons/md';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { DateRange, longDateFormatProps } from '@/components/dateFormats';
import { Outlet, useLoaderData, useLocation } from 'react-router-dom';
import { ArticlePreTitle } from '../components/articlePreTitle';
import { ArticleSkeleton } from '../components/articleSkeleton';
import { ArticleTextContainer } from '../components/articleTextContainer';
import { ArticleTitle } from '../components/articleTitle';
import { FundingBanner } from '../components/fundingBanner';
import { PageContainer } from '../components/pageContainer';
import {
  createResourceLoaderWithRedirect,
  ResourceLoaderResult,
} from '../createResourceLoaderWithRedirect';
import { useNotifyOfPartialDataIfErrors } from '@/routes/rootErrorPage';
import PageMetaData from '@/components/PageMetaData';
import { apiConstants } from '@/config/apiConstants';

export const ProjectPageSkeleton = ArticleSkeleton;

fragmentManager.register(/* GraphQL */ `
  fragment ProjectPage on GbifProject {
    # Define the values used by this page
    title
    excerpt
    status
    start
    end
    fundsAllocated
    primaryLink {
      label
      url
    }
    ...ProjectFundingBanner
    # The Project About tab uses the data from this loader and defines its own data needs in this fragment
    ...ProjectAboutTab
  }
`);

export const projectPageLoader = createResourceLoaderWithRedirect({
  resourceType: 'GbifProject',
  query: /* GraphQL */ `
    query Project($key: String!) {
      resource(id: $key) {
        ...ResourceRedirectDetails
        ... on GbifProject {
          ...ProjectPage
        }
      }
      # The Project Datasets tab also uses some data from this loader and defines its own data needs in this fragment
      ...ProjectDatasetsTab
    }
  `,
});

export function ProjectPage() {
  const { data, errors } = useLoaderData() as ResourceLoaderResult<ProjectPageFragment>;
  useNotifyOfPartialDataIfErrors(errors);
  const { resource } = data;
  const {
    count: occurrenceCount,
    loading: occurrenceLoading,
    error: occurrenceError,
  } = useCount({
    apiEndpoint: apiConstants.occurrenceSearch,
    params: { projectId: [resource.projectId!] },
  });
  const {
    count: citationsCount,
    loading: citationsLoading,
    error: citationsError,
  } = useLiteratureCount({
    predicate: {
      type: 'equals',
      key: 'gbifProjectIdentifier',
      value: resource.projectId,
    },
  });

  const tabLinks = createTabLinks(resource);
  const location = useLocation();

  return (
    <article>
      <PageMetaData
        title={resource.title}
        description={resource.excerpt}
        path={location.pathname}
        imageUrl={resource.primaryImage?.file.normal}
        imageAlt={resource.primaryImage?.description}
      />

      <PageContainer topPadded bottomPadded className="g-bg-white">
        <ArticleTextContainer>
          <ArticlePreTitle
            clickable
            secondary={<FormattedMessage id={`enums.cms.projectStatus.${resource.status}`} />}
          >
            <DynamicLink to="/resource/search?contentType=project">
              <FormattedMessage id="cms.contentType.project" />
            </DynamicLink>
          </ArticlePreTitle>

          <ArticleTitle dir="auto" dangerouslySetTitle={{ __html: resource.title }} />

          {/* <RenderIfChildren className="g-mt-2 g-text-slate-500 dark:g-text-gray-400 g-text-sm g-font-medium g-flex g-items-center g-gap-6"> */}
          <FeatureList className="g-text-slate-500 dark:g-text-gray-400">
            {resource.start && resource.end && (
              <GenericFeature>
                <CalendarIcon size={18} />
                <DateRange
                  from={new Date(resource.start)}
                  to={new Date(resource.end)}
                  {...longDateFormatProps}
                />
              </GenericFeature>
            )}

            {resource.fundsAllocated && (
              <GenericFeature>
                <EuroIcon size={18} />
                <FormattedNumber value={resource.fundsAllocated} />
              </GenericFeature>
            )}

            {!!citationsCount && !citationsLoading && !citationsError && (
              <GenericFeature className="g-underline">
                <CitationIcon />
                <DynamicLink
                  to="/literature/search"
                  pageId="literatureSearch"
                  searchParams={{ gbifProjectIdentifier: resource.projectId }}
                >
                  <FormattedMessage id="counts.nCitations" values={{ total: citationsCount }} />
                </DynamicLink>
              </GenericFeature>
            )}
            {(occurrenceCount ?? 0) > 0 && !occurrenceLoading && !occurrenceError && (
              <GenericFeature className="g-underline">
                <OccurrenceIcon />
                <DynamicLink
                  to="/occurrence/search"
                  pageId="occurrenceSearch"
                  searchParams={{ projectId: resource.projectId }}
                >
                  <FormattedMessage id="counts.nOccurrences" values={{ total: occurrenceCount }} />
                </DynamicLink>
              </GenericFeature>
            )}
          </FeatureList>
          {/* </RenderIfChildren> */}

          <Tabs className="g-mt-6" links={tabLinks} />
        </ArticleTextContainer>

        <Outlet />
      </PageContainer>

      <FundingBanner resource={resource} />
    </article>
  );
}

const staticTabLinks: Array<{ to: string; children: React.ReactNode }> = [
  { to: '.', children: <FormattedMessage id="cms.resource.about" /> },
  { to: 'datasets', children: <FormattedMessage id="cms.resource.datasets" /> },
  { to: 'news', children: <FormattedMessage id="cms.project.newsAndEvents" /> },
];

function createTabLinks(resource: ProjectPageFragment) {
  const tabLinks = [...staticTabLinks];

  if (resource?.primaryLink) {
    tabLinks.push({
      to: resource.primaryLink.url,
      children: (
        <span className="g-flex g-items-center">
          {resource.primaryLink.label} <MdLink className="g-ms-1" />
        </span>
      ),
    });
  }

  return tabLinks;
}
